import ChartCard from "../../components/ui/ChartCard";
import { CeoTextModalButton, CeoTextPanel } from "../../components/ui/CeoTextAnswers";
import { buildStackedBarOptions } from "./chartOptions";

const normalizeCountItems = (items = []) =>
  items.map((item) => ({
    label: item?.label || "Onbekend",
    count: Number(item?.count) || 0,
    count_rel: Number(item?.count_rel) || 0,
  }));

const normalizeStackedSeries = (series = []) =>
  series.map((serie) => ({
    name: serie?.name || "",
    data: Array.isArray(serie?.data)
      ? serie.data.map((value) => Number(value) || 0)
      : [],
  }));

const flattenHeatmapValues = (series = []) => {
  const values = [];
  series.forEach((serie) => {
    if (Array.isArray(serie?.data)) {
      serie.data.forEach((value) => values.push(Number(value) || 0));
    }
  });
  return values;
};

const buildHeatmapRanges = (mode) =>
  mode === "rel"
    ? [
        { from: 0, to: 2, name: "0-2%", color: "#e0e7ff" },
        { from: 2, to: 4, name: "2-4%", color: "#c7d2fe" },
        { from: 4, to: 6, name: "4-6%", color: "#a5b4fc" },
        { from: 6, to: 8, name: "6-8%", color: "#818cf8" },
        { from: 8, to: Number.MAX_SAFE_INTEGER, name: "8% +", color: "#3730a3" },
      ]
    : [
        { from: 0, to: 4, name: "0-4", color: "#e0e7ff" },
        { from: 5, to: 9, name: "5-9", color: "#c7d2fe" },
        { from: 10, to: 14, name: "10-14", color: "#a5b4fc" },
        { from: 15, to: 19, name: "15-19", color: "#818cf8" },
        { from: 20, to: Number.MAX_SAFE_INTEGER, name: "20 +", color: "#3730a3" },
      ];

const heatmapLabelColor = (value, maxValue) => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return "#4b5563";
  }
  const ratio = value / maxValue;
  if (ratio >= 0.6) {
    return "#f9fafb";
  }
  if (ratio >= 0.3) {
    return "#1f2937";
  }
  return "#4b5563";
};

const scheduleLabelColorUpdate = (chartCtx, maxValue) => {
  if (!chartCtx?.el) {
    return;
  }
  requestAnimationFrame(() => {
    const nodes = chartCtx.el.querySelectorAll(".apexcharts-datalabel");
    nodes.forEach((node) => {
      const raw = (node.textContent || "").replace("%", "");
      const value = Number(raw) || 0;
      const fill = heatmapLabelColor(value, maxValue);
      node.setAttribute("fill", fill);
    });
  });
};

const buildDataSource = (data = {}) => {
  const metrics = data?.jeugdhulpMetrics || {};
  const duurCounts = Array.isArray(data?.jeugdhulpDuurCounts)
    ? normalizeCountItems(data.jeugdhulpDuurCounts)
    : [];
  const snelheidCounts = Array.isArray(data?.jeugdhulpSnelheidCounts)
    ? normalizeCountItems(data.jeugdhulpSnelheidCounts)
    : [];

  const heatmap =
    metrics.duur_vs_snelheid && Array.isArray(metrics.duur_vs_snelheid.series)
      ? {
          ...metrics.duur_vs_snelheid,
          series_rel: Array.isArray(metrics.duur_vs_snelheid.series_rel)
            ? metrics.duur_vs_snelheid.series_rel
            : [],
        }
      : { duur_categories: [], snelheid_categories: [], series: [], series_rel: [] };

  const hulpSoorten = Array.isArray(metrics.hulp_soorten)
    ? normalizeCountItems(metrics.hulp_soorten)
    : [];

  const hulpSoortenPerDoelgroep =
    metrics.hulp_soorten_per_doelgroep &&
    Array.isArray(metrics.hulp_soorten_per_doelgroep.series)
      ? {
          categories: metrics.hulp_soorten_per_doelgroep.categories || [],
          series: normalizeStackedSeries(metrics.hulp_soorten_per_doelgroep.series),
        }
      : { categories: [], series: [] };

  const ervaringPerHulpSoort =
    metrics.ervaring_per_hulp_soort &&
    Array.isArray(metrics.ervaring_per_hulp_soort.series)
      ? {
          categories: metrics.ervaring_per_hulp_soort.categories || [],
          series: normalizeStackedSeries(metrics.ervaring_per_hulp_soort.series),
          series_rel: normalizeStackedSeries(
            metrics.ervaring_per_hulp_soort.series_rel || []
          ),
        }
      : { categories: [], series: [], series_rel: [] };

  return {
    heatmap,
    hulpSoorten,
    hulpSoortenPerDoelgroep,
    ervaringPerHulpSoort,
    duurCounts,
    snelheidCounts,
  };
};

export default function JeugdhulpTab({ data, mode = "abs", showTextAnswers = false }) {
  const textAnswers = data?.ceoTextAnswers || {};
  const {
    heatmap,
    hulpSoorten,
    hulpSoortenPerDoelgroep,
    ervaringPerHulpSoort,
    duurCounts,
    snelheidCounts,
  } = buildDataSource(data);
  const heatmapAbsSeries = heatmap.series;
  const heatmapRelSeries = heatmap.series_rel;
  const heatmapSeries = mode === "rel" ? heatmapRelSeries : heatmapAbsSeries;
  const heatmapValues = flattenHeatmapValues(heatmapSeries);
  const heatmapMax = heatmapValues.length
    ? Math.max(...heatmapValues)
    : 0;
  const heatmapRanges = buildHeatmapRanges(mode);

  const heatmapOptions = {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
      events: {
        mounted: (ctx) => scheduleLabelColorUpdate(ctx, heatmapMax),
        updated: (ctx) => scheduleLabelColorUpdate(ctx, heatmapMax),
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.45,
        colorScale: {
          ranges: heatmapRanges,
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontWeight: 600,
        colors: ["#4b5563"],
      },
      formatter: (val) =>
        mode === "rel" ? `${Number(val).toFixed(1)}%` : `${Number(val) || 0}`,
    },
    xaxis: {
      categories: heatmap.snelheid_categories,
      title: { text: "Snelheid aanbod" },
    },
    yaxis: {
      categories: heatmap.duur_categories,
      title: { text: "Duur jeugdhulp" },
    },
    colors: ["#6366F1"],
    tooltip: {
      y: {
        formatter: (_val, opts) => {
          const sIdx = opts?.seriesIndex ?? 0;
          const dIdx = opts?.dataPointIndex ?? 0;
          const abs = heatmapAbsSeries?.[sIdx]?.data?.[dIdx] ?? 0;
          const rel = heatmapRelSeries?.[sIdx]?.data?.[dIdx] ?? 0;
          return `${abs} (${rel}%)`;
        },
      },
    },
  };

  const donutLabels = hulpSoorten.map((item) => item.label);
  const donutAbsSeries = hulpSoorten.map((item) => item.count);
  const donutRelSeries = hulpSoorten.map((item) => item.count_rel);
  const donutSeries = mode === "rel" ? donutRelSeries : donutAbsSeries;
  const donutTotal = donutAbsSeries.reduce((sum, value) => sum + (Number(value) || 0), 0);

  const donutOptions = {
    chart: { type: "donut" },
    labels: donutLabels,
    legend: { position: "bottom" },
    dataLabels: {
      enabled: true,
      formatter: (_val, opts) => {
        const idx = opts?.seriesIndex ?? 0;
        const abs = donutAbsSeries[idx] ?? 0;
        const rel = donutRelSeries[idx] ?? 0;
        return mode === "rel" ? `${Number(rel).toFixed(1)}%` : `${abs}`;
      },
    },
    tooltip: {
      y: {
        formatter: (_val, opts) => {
          const idx = opts?.seriesIndex ?? 0;
          const abs = donutAbsSeries[idx] ?? 0;
          const rel = donutRelSeries[idx] ?? 0;
          return `${abs} (${rel}%)`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
              offsetY: 16,
            },
            value: {
              show: true,
              fontSize: "20px",
              fontWeight: 700,
              color: "#111827",
              offsetY: -16,
              formatter: (_val, opts) => {
                const idx = opts?.seriesIndex ?? 0;
                const abs = donutAbsSeries[idx] ?? 0;
                const rel = donutRelSeries[idx] ?? 0;
                return mode === "rel" ? `${Number(rel).toFixed(1)}%` : `${abs}`;
              },
            },
            total: {
              show: true,
              label: "Totaal",
              formatter: () => donutTotal,
            },
          },
        },
      },
    },
  };

  const duurLabels = duurCounts.map((item) => item.label);
  const duurAbsValues = duurCounts.map((item) => item.count);
  const duurRelValues = duurCounts.map((item) => item.count_rel);
  const duurValues = mode === "rel" ? duurRelValues : duurAbsValues;

  const duurOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    xaxis: {
      categories: duurLabels,
    },
    yaxis: {
      labels: {
        trim: false,
        maxWidth: 320,
      },
    },
    fullscreenOverrides: {
      grid: {
        padding: {
          left: 60,
          right: 20,
          top: 8,
          bottom: 72,
        },
      },
      yaxis: {
        labels: {
          trim: false,
          maxWidth: 420,
          style: {
            fontSize: "22px",
          },
        },
      },
      xaxis: {
        axisBorder: {
          offsetY: 6,
        },
        axisTicks: {
          offsetY: 6,
        },
        labels: {
          offsetY: 16,
          style: {
            fontSize: "16px",
          },
        },
      },
      dataLabels: {
        style: {
          fontSize: "17px",
        },
      },
    },
    colors: ["#6366F1"],
    dataLabels: {
      enabled: true,
      formatter: (val) =>
        mode === "rel" ? `${Number(val).toFixed(1)}%` : `${val}`,
    },
    tooltip: {
      y: {
        formatter: (_val, opts) => {
          const idx = opts?.dataPointIndex ?? 0;
          const abs = duurAbsValues[idx] ?? 0;
          const rel = duurRelValues[idx] ?? 0;
          return `${abs} (${rel}%)`;
        },
      },
    },
  };

  const duurSeries = [{ name: "Aantal antwoorden", data: duurValues }];

  const snelheidLabels = snelheidCounts.map((item) => item.label);
  const snelheidAbsValues = snelheidCounts.map((item) => item.count);
  const snelheidRelValues = snelheidCounts.map((item) => item.count_rel);
  const snelheidValues = mode === "rel" ? snelheidRelValues : snelheidAbsValues;

  const snelheidOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
    xaxis: {
      categories: snelheidLabels,
    },
    yaxis: {
      labels: {
        trim: false,
        maxWidth: 320,
      },
    },
    fullscreenOverrides: {
      grid: {
        padding: {
          left: 60,
          right: 20,
          top: 8,
          bottom: 20,
        },
      },
      yaxis: {
        labels: {
          trim: false,
          maxWidth: 420,
          style: {
            fontSize: "22px",
          },
        },
      },
      xaxis: {
        labels: {
          style: {
            fontSize: "16px",
          },
        },
      },
      dataLabels: {
        style: {
          fontSize: "17px",
        },
      },
    },
    colors: ["#14b8a6"],
    dataLabels: {
      enabled: true,
      formatter: (val) =>
        mode === "rel" ? `${Number(val).toFixed(1)}%` : `${val}`,
    },
    tooltip: {
      y: {
        formatter: (_val, opts) => {
          const idx = opts?.dataPointIndex ?? 0;
          const abs = snelheidAbsValues[idx] ?? 0;
          const rel = snelheidRelValues[idx] ?? 0;
          return `${abs} (${rel}%)`;
        },
      },
    },
  };

  const snelheidSeries = [{ name: "Aantal antwoorden", data: snelheidValues }];

  const doelgroepOptions = buildStackedBarOptions({
    categories: hulpSoortenPerDoelgroep.categories,
    colors: ["#7E22CE", "#3B82F6"],
    horizontal: true,
  });

  const ervaringPerSoortOptions = buildStackedBarOptions({
    categories: ervaringPerHulpSoort.categories,
    colors: ["#16a34a", "#84cc16", "#eab308", "#f97316", "#ef4444"],
    horizontal: true,
    fillOpacity: 0.9,
    stackType: mode === "rel" ? "100%" : "normal",
    tooltipFormatter: (_val, opts) => {
      const sIdx = opts?.seriesIndex ?? 0;
      const dIdx = opts?.dataPointIndex ?? 0;
      const abs = ervaringPerHulpSoort?.series?.[sIdx]?.data?.[dIdx] ?? 0;
      const rel = ervaringPerHulpSoort?.series_rel?.[sIdx]?.data?.[dIdx] ?? 0;
      return `${abs} (${rel}%)`;
    },
  });

  return (
    <div className="container">
      <div className="row row-split">
        <ChartCard
          title="Snelheid aanbod (per categorie)"
          options={snelheidOptions}
          series={snelheidSeries}
          type="bar"
          info="Hoe snel kreeg je de hulp die je nodig had?"
        />
        <ChartCard
          title="Duur jeugdhulp (per categorie)"
          options={duurOptions}
          series={duurSeries}
          type="bar"
          info="Hoelang heb je jeugdhulp gehad?"
        />

      </div>
      <div className="row">
        <ChartCard
          title="Duur jeugdhulp × Snelheid aanbod"
          options={heatmapOptions}
          series={heatmapSeries}
          type="heatmap"
          info="Hoelang heb je jeugdhulp gehad? Hoe snel kreeg je de hulp die je nodig had?"
        />
      </div>

      

      <div className="row row-split">
        <ChartCard
          title="Soorten hulp (totaal)"
          containerClassName="ceo-text-chart"
          options={donutOptions}
          series={donutSeries}
          type="donut"
          info="Over welke soorten hulp ging je ervaring?"
          headerActions={showTextAnswers ? <CeoTextModalButton title="Anders, namelijk: soorten hulp" items={textAnswers.hulp_soorten_anders || []} /> : null}
        />
        {/* <ChartCard
          title="Soorten hulp per doelgroep"
          options={doelgroepOptions}
          series={hulpSoortenPerDoelgroep.series}
          type="bar"
          info="Combinatie: Oudere/verzorger of Jongere & Over welke soorten hulp ging je ervaring?"
        /> */}
        <ChartCard
          title="Ervaring per soort hulp"
          options={ervaringPerSoortOptions}
          series={mode === "rel" ? ervaringPerHulpSoort.series_rel : ervaringPerHulpSoort.series}
          type="bar"
          info="Ervaring per soort hulp."
          headerActions={showTextAnswers ? <CeoTextModalButton title="Anders, namelijk: soorten hulp" items={textAnswers.hulp_soorten_anders || []} /> : null}
        />
      </div>
      {showTextAnswers && <CeoTextPanel title="Heb je nog vragen of opmerkingen?" items={textAnswers.extra_opmerking || []} onPage />}
    </div>
  );
}
